// src/components/Rules/RuleForm.js — Create / Edit rule dialog (F6)
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, Select, FormControl,
  InputLabel, IconButton, Box, Typography, RadioGroup,
  FormControlLabel, Radio, Chip, Divider, Alert,
  CircularProgress, Tooltip
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useBudget } from '../../contexts/BudgetContext';
import api from '../../services/api';

const FIELD_OPTIONS = [
  { value: 'description', label: 'Description' },
  { value: 'merchant',    label: 'Merchant'     },
  { value: 'amount',      label: 'Amount'       },
  { value: 'accountId',  label: 'Account ID'   }
];

const OPERATOR_OPTIONS = {
  description: ['contains', 'startsWith', 'endsWith', 'equals'],
  merchant:    ['contains', 'startsWith', 'endsWith', 'equals'],
  amount:      ['equals', 'gt', 'lt'],
  accountId:   ['equals']
};

const OPERATOR_LABELS = {
  contains:   'contains',
  startsWith: 'starts with',
  endsWith:   'ends with',
  equals:     'equals',
  gt:         'greater than',
  lt:         'less than'
};

const emptyCondition = () => ({ field: 'description', operator: 'contains', value: '' });

const RuleForm = ({ open, onClose, onSaved, editingRule }) => {
  const { categories } = useBudget();

  const [name,           setName]           = useState('');
  const [conditions,     setConditions]     = useState([emptyCondition()]);
  const [conditionLogic, setConditionLogic] = useState('AND');
  const [categoryId,     setCategoryId]     = useState('');
  const [tags,           setTags]           = useState([]);
  const [tagInput,       setTagInput]       = useState('');
  const [isActive,       setIsActive]       = useState(true);
  const [priority,       setPriority]       = useState(0);

  // Test panel
  const [testDesc,    setTestDesc]    = useState('');
  const [testMerchant,setTestMerchant]= useState('');
  const [testAmount,  setTestAmount]  = useState('');
  const [testResult,  setTestResult]  = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  // ── Populate form when editing ──────────────────────────────────────────────
  useEffect(() => {
    if (editingRule) {
      setName(editingRule.name || '');
      setConditions(
        editingRule.conditions?.length
          ? editingRule.conditions.map(c => ({ ...c }))
          : [emptyCondition()]
      );
      setConditionLogic(editingRule.conditionLogic || 'AND');
      setCategoryId(editingRule.actions?.categoryId || '');
      setTags(editingRule.actions?.tags || []);
      setIsActive(editingRule.isActive !== undefined ? editingRule.isActive : true);
      setPriority(editingRule.priority || 0);
    } else {
      resetForm();
    }
    setError('');
    setTestResult(null);
  }, [editingRule, open]);

  const resetForm = () => {
    setName('');
    setConditions([emptyCondition()]);
    setConditionLogic('AND');
    setCategoryId('');
    setTags([]);
    setTagInput('');
    setIsActive(true);
    setPriority(0);
    setTestDesc('');
    setTestMerchant('');
    setTestAmount('');
    setTestResult(null);
    setError('');
  };

  // ── Conditions ──────────────────────────────────────────────────────────────
  const updateCondition = (idx, key, val) => {
    setConditions(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      // Reset operator if it's not valid for the new field
      if (key === 'field') {
        const validOps = OPERATOR_OPTIONS[val] || [];
        if (!validOps.includes(next[idx].operator)) {
          next[idx].operator = validOps[0] || 'contains';
        }
      }
      return next;
    });
  };

  const addCondition    = ()    => setConditions(prev => [...prev, emptyCondition()]);
  const removeCondition = (idx) => setConditions(prev => prev.filter((_, i) => i !== idx));

  // ── Tags ────────────────────────────────────────────────────────────────────
  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!name.trim()) { setError('Rule name is required'); return; }
    if (!categoryId)  { setError('Please select a category action'); return; }
    if (conditions.some(c => !c.value || String(c.value).trim() === '')) {
      setError('All condition values must be filled in');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        conditions,
        conditionLogic,
        actions: { categoryId, tags },
        isActive,
        priority: Number(priority)
      };

      if (editingRule) {
        await api.put(`/rules/${editingRule._id}`, payload);
      } else {
        await api.post('/rules', payload);
      }
      onSaved();
      onClose();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  // ── Test (only works when editing an existing rule) ─────────────────────────
  const handleTest = async () => {
    if (!editingRule) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/rules/${editingRule._id}/test`, {
        description: testDesc,
        merchant:    testMerchant,
        amount:      testAmount !== '' ? Number(testAmount) : undefined
      });
      setTestResult(data);
    } catch (err) {
      setTestResult({ error: err.response?.data?.message || 'Test failed' });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>
        {editingRule ? 'Edit Rule' : 'Create Auto-categorization Rule'}
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Rule name ──────────────────────────────────────── */}
        <TextField
          label="Rule name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        {/* ── Conditions ────────────────────────────────────── */}
        <Typography variant="subtitle2" gutterBottom>
          Conditions
        </Typography>

        {/* AND / OR toggle */}
        <RadioGroup
          row
          value={conditionLogic}
          onChange={e => setConditionLogic(e.target.value)}
          sx={{ mb: 1 }}
        >
          <FormControlLabel value="AND" control={<Radio size="small" />} label="Match ALL (AND)" />
          <FormControlLabel value="OR"  control={<Radio size="small" />} label="Match ANY (OR)"  />
        </RadioGroup>

        {conditions.map((cond, idx) => (
          <Box key={idx} display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
            {/* Field */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={cond.field}
                label="Field"
                onChange={e => updateCondition(idx, 'field', e.target.value)}
              >
                {FIELD_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Operator */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                value={cond.operator}
                label="Operator"
                onChange={e => updateCondition(idx, 'operator', e.target.value)}
              >
                {(OPERATOR_OPTIONS[cond.field] || []).map(op => (
                  <MenuItem key={op} value={op}>{OPERATOR_LABELS[op] || op}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Value */}
            <TextField
              label="Value"
              size="small"
              value={cond.value}
              onChange={e => updateCondition(idx, 'value', e.target.value)}
              type={cond.field === 'amount' ? 'number' : 'text'}
              sx={{ flex: 1, minWidth: 120 }}
            />

            {/* Remove */}
            {conditions.length > 1 && (
              <Tooltip title="Remove condition">
                <IconButton size="small" onClick={() => removeCondition(idx)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={addCondition}
          variant="outlined"
          sx={{ mb: 3 }}
        >
          Add condition
        </Button>

        <Divider sx={{ mb: 2 }} />

        {/* ── Actions ───────────────────────────────────────── */}
        <Typography variant="subtitle2" gutterBottom>
          Action — Set category to:
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Category *</InputLabel>
          <Select
            value={categoryId}
            label="Category *"
            onChange={e => setCategoryId(e.target.value)}
          >
            {categories.map(cat => (
              <MenuItem key={cat.id || cat._id} value={cat.id || cat._id}>
                {cat.icon ? `${cat.icon} ` : ''}{cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tags */}
        <Typography variant="subtitle2" gutterBottom>
          Also add tags (optional):
        </Typography>
        <Box display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
          <TextField
            label="Tag"
            size="small"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            sx={{ minWidth: 160 }}
          />
          <Button size="small" variant="outlined" onClick={addTag}>Add</Button>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
          {tags.map(tag => (
            <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)} />
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Settings ──────────────────────────────────────── */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
          <FormControlLabel
            control={
              <Radio
                checked={isActive}
                onClick={() => setIsActive(v => !v)}
                size="small"
              />
            }
            label="Active"
          />
          <TextField
            label="Priority"
            type="number"
            size="small"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            helperText="Higher = applied first"
            sx={{ width: 120 }}
          />
        </Box>

        {/* ── Test panel (edit mode only) ───────────────────── */}
        {editingRule && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Test this rule
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              <TextField label="Description" size="small" value={testDesc}
                onChange={e => setTestDesc(e.target.value)} sx={{ flex: 1, minWidth: 140 }} />
              <TextField label="Merchant"    size="small" value={testMerchant}
                onChange={e => setTestMerchant(e.target.value)} sx={{ flex: 1, minWidth: 140 }} />
              <TextField label="Amount"      size="small" type="number" value={testAmount}
                onChange={e => setTestAmount(e.target.value)} sx={{ width: 110 }} />
              <Button
                variant="contained"
                size="small"
                startIcon={testLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleTest}
                disabled={testLoading}
              >
                Run test
              </Button>
            </Box>
            {testResult && !testResult.error && (
              <Alert severity={testResult.matches ? 'success' : 'warning'} sx={{ mt: 1 }}>
                {testResult.matches
                  ? `✅ MATCH — category will be set automatically`
                  : '❌ No match — rule conditions not satisfied'}
              </Alert>
            )}
            {testResult?.error && (
              <Alert severity="error" sx={{ mt: 1 }}>{testResult.error}</Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {editingRule ? 'Save changes' : 'Create rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleForm;
